// Units and pace math for cardio, with the units always in the text: distances in m or km, pace in min per
// km (or per 100 m swimming, per 500 m rowing), speed in km/h, durations as "45 min" or "1 h 05 min".
// Numbers use Swiss formatting (12'345, a point for decimals).
import type { CardioSport } from '../types';
import { CARDIO_SPORTS, type SpeedMeasure } from './sports';

/** Swiss number formatting: 12'345, decimals with a point. */
export function swissNumber(n: number, digits = 0): string {
  const fixed = Math.abs(n).toFixed(digits);
  const [int, frac] = fixed.split('.');
  const grouped = int!.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `${n < 0 && Number(fixed) !== 0 ? '-' : ''}${grouped}${frac ? `.${frac}` : ''}`;
}

const finitePositive = (n: number | undefined | null): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;

/** Seconds per km from a distance and a time; null when either is missing or zero. */
export function paceSecPerKm(distanceM: number | undefined, durationSec: number | undefined): number | null {
  return finitePositive(distanceM) && finitePositive(durationSec) ? durationSec / (distanceM / 1000) : null;
}

/** km/h from a distance and a time; null when either is missing or zero. */
export function speedKmh(distanceM: number | undefined, durationSec: number | undefined): number | null {
  return finitePositive(distanceM) && finitePositive(durationSec) ? (distanceM / 1000) / (durationSec / 3600) : null;
}

export const paceToSpeed = (secPerKm: number): number => 3600 / secPerKm;
export const speedToPace = (kmh: number): number => 3600 / kmh;

/** Seconds for a distance at a pace (s per km). */
export const timeAtPace = (distanceM: number, secPerKm: number): number => (distanceM / 1000) * secPerKm;

/** "4:30" from 270 seconds; "1:02:05" from an hour or more. Rounded to whole seconds. */
export function clock(totalSec: number): string {
  const s = Math.max(0, Math.round(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(sec)}` : `${m}:${two(sec)}`;
}

/** "45 min", "1 h 05 min", "40 s". */
export function formatDuration(totalSec: number): string {
  const s = Math.max(0, Math.round(totalSec));
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h === 0) return s % 60 && s < 600 ? `${clock(s)} min` : `${Math.round(s / 60)} min`;
  return m === 60 ? `${h + 1} h 00 min` : `${h} h ${String(m).padStart(2, '0')} min`;
}

/**
 * "800 m", "1'500 m", "5 km", "21.1 km". Swimming and rowing (meters: true) stay in m up to 10 km.
 * Under 1 km always m.
 */
export function formatDistance(meters: number, opts: { meters?: boolean } = {}): string {
  const m = Math.max(0, meters);
  if (m < 1000 || (opts.meters && m < 10_000)) return `${swissNumber(Math.round(m))} m`;
  // Up to two decimals, trailing zeros left out: 5 km, 21.1 km, 42.19 km; whole km from 100 km.
  const hundredths = Math.round(m / 10);
  const digits = m >= 100_000 ? 0 : hundredths % 10 !== 0 ? 2 : hundredths % 100 !== 0 ? 1 : 0;
  return `${swissNumber(m / 1000, digits)} km`;
}

/** "4:30 min/km". */
export const formatPace = (secPerKm: number): string => `${clock(secPerKm)} min/km`;

/** "28.5 km/h". */
export const formatSpeed = (kmh: number): string => `${swissNumber(kmh, kmh >= 100 ? 0 : 1)} km/h`;

/**
 * Speed in the sport's own measure: "4:30 min/km" running, "1:55 min/100 m" swimming, "2:05 min/500 m"
 * rowing, "28.5 km/h" cycling. Input as seconds per km.
 */
export function formatSportSpeed(secPerKm: number, measure: SpeedMeasure): string {
  switch (measure) {
    case 'pace_km': return formatPace(secPerKm);
    case 'pace_100m': return `${clock(secPerKm / 10)} min/100 m`;
    case 'pace_500m': return `${clock(secPerKm / 2)} min/500 m`;
    case 'speed': return formatSpeed(paceToSpeed(secPerKm));
  }
}

/** The speed of a finished activity in its sport's measure, or null without distance or time. */
export function activitySpeedText(sport: CardioSport, distanceM: number | undefined, durationSec: number | undefined): string | null {
  const pace = paceSecPerKm(distanceM, durationSec);
  return pace === null ? null : formatSportSpeed(pace, CARDIO_SPORTS[sport].measure);
}

/**
 * Reads a time typed by a person: "45" (minutes), "45:30" (minutes and seconds), "1:05:30", "1h 05", "90 min",
 * "40 s". Returns seconds, or null when it is not a time.
 */
export function parseDuration(text: string): number | null {
  const t = text.trim().toLowerCase().replace(',', '.');
  if (!t) return null;
  let m = /^(\d+):(\d{1,2}):(\d{1,2})$/.exec(t);
  if (m) return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]);
  m = /^(\d+):(\d{1,2})$/.exec(t);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = /^(\d+(?:\.\d+)?)\s*h(?:ours?)?\s*(?:(\d+)\s*(?:m|min|minutes?)?)?$/.exec(t);
  if (m) return Math.round(Number(m[1]) * 3600 + Number(m[2] ?? 0) * 60);
  m = /^(\d+(?:\.\d+)?)\s*(m|min|mins|minutes?)?$/.exec(t);
  if (m) return Math.round(Number(m[1]) * 60);
  m = /^(\d+(?:\.\d+)?)\s*(s|sec|secs|seconds?)$/.exec(t);
  if (m) return Math.round(Number(m[1]));
  return null;
}

/** Reads a pace "4:30" (min:s per km) or "4.5" (minutes per km). Seconds per km, or null. */
export function parsePace(text: string): number | null {
  const t = text.trim().replace(',', '.').replace(/\s*min\s*\/\s*km$/i, '');
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (m) return Number(m[2]) < 60 ? Number(m[1]) * 60 + Number(m[2]) : null;
  const n = Number(t);
  return t && Number.isFinite(n) && n > 0 ? Math.round(n * 60) : null;
}

/** Reads a distance: "800 m", "5 km", "10.5" (km), "1'500 m". Meters, or null. */
export function parseDistance(text: string, defaultUnit: 'km' | 'm' = 'km'): number | null {
  const t = text.trim().toLowerCase().replace(/'/g, '').replace(',', '.');
  const m = /^(\d+(?:\.\d+)?)\s*(km|m)?$/.exec(t);
  if (!m) return null;
  const n = Number(m[1]);
  const unit = m[2] ?? defaultUnit;
  return Math.round(unit === 'km' ? n * 1000 : n);
}

/** Great-circle distance in m between two points (lat, lon in degrees). */
export function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6_371_008.8;
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}
